
<script context="module" lang="ts">
    import { writable } from 'svelte/store';

    type ToastType = "success" | "failure" | "info";

    export interface ToastMessage {
        message: string;
        type?: ToastType;
        title?: string;
        duration?: number;
        _ref?: HTMLElement;
        _progressRef?: HTMLElement;
        _animatingIn?: Animation;
        _animatingOut?: Animation; 
    };
    export const toast = (toast: ToastMessage) => {
        if (toast.type === 'success' && !toast.duration) {
            toast.duration = defaultSuccessDuration;
        }
        toasts.update(t => {
            if (t.length < maxToasts) {
                return [...t, toast];
            } else {
                return [...t.slice(1), toast]; // Skip the oldest toast.
            }
        });
    };
    export const removeToast = (message: ToastMessage) => {
        toasts.update(t => {
            const i = t.findIndex(x => x === message);
            // NOTE: It is apparently important to NOT modify the array by using splice, 
            // but instead to use filter to produce a new array, if you want Svelte to re-render correctly and not crash.
            return t.filter((_item, idx) => idx !== i); 
        });
    };
    export const clearToasts = (type?: ToastType) => {
        toasts.update(t => {
            const newToasts =  type ? t.filter(x => x.type !== type) : [];
            return newToasts;
        });
    };
    const toasts = writable<ToastMessage[]>([]);
    export const maxToasts = 5;
    export let defaultSuccessDuration = 3_000;

    // // Examples used for testing.
    // toast({message: "Preferences saved! Preferences saved! Preferences saved! Preferences saved! Preferences saved! Preferences saved! Preferences saved! Preferences saved! Preferences saved! Preferences saved! ", type: 'success', duration: 2000});
    // toast({message: "Preferences saved!", type: 'failure', duration: 2000});
    // toast({message: "Preferences saved!", duration: 2000});
    // toast({message: "Preferences saved!", duration: 2000});
    // toast({message: "Preferences saved!", duration: 2000});
    // setTimeout(() => {
    //     toast({message: "Preferences saved!", duration: 0, type: 'success'});
    // }, 1000);
</script>

<script lang="ts">
    import Button from './Button.svelte';
    import CloseIcon from '../icons/CloseIcon.svelte';

    const animateOut = (toast: ToastMessage) => {
        if (toast.duration) {
            toast._animatingOut = toast._progressRef?.animate([
                    { transform: 'translateX(0)' },
                    { transform: 'translateX(-100%)' },
                ], { duration: toast.duration });
            toast._animatingOut!.onfinish = () => removeToast(toast);
        }
    };

    $: for (let toast of $toasts) {
        if (toast._ref && !toast._animatingIn) {
            toast._animatingIn = toast._ref?.animate([
                { opacity: 0 },
                { opacity: 1 },
            ], { duration: 1000 });
        }
        if (toast._progressRef && !toast._animatingOut) {
            animateOut(toast);
        }
    }    

    const onHover = (toast: ToastMessage) => {
        toast._animatingOut?.cancel();
    };
    const onMouseOut = (toast: ToastMessage) => {
        animateOut(toast);
    };
</script>

<div class="fixed bottom-[30px] left-[50%] flex flex-col gap-2 max-h-[80%] overflow-auto translate-x-[-50%]">
{#each $toasts as toast}
<div class="rounded z-10  text-center 
    shadow-md
    w-[400px]
    max-w-[90vw]
     border-solid border-2 border-gray-300
    {toast.type === 'success' ? 'text-black bg-vv-teal-lightest border-vv-teal-light'
    :toast.type === 'failure' ? 'text-white bg-vv-red-dark border-vv-red-light'
    : 'text-black bg-vv-grey-mid-light'}
" 
    bind:this={toast._ref}
    on:mouseover={() => onHover(toast)}
    on:focus={() => onHover(toast)}
    on:mouseout={() => onMouseOut(toast)}
    on:blur={() => onMouseOut(toast)}
    role="alert">
    <div class="px-4 py-2 flex" >
        <div class="flex-1">{toast.message}</div>
        <div class="flex-initial">
            <Button type="tertiary" class="!min-h-[24px] 
                {toast.type === 'failure' ? 'hover:bg-vv-red-danger-dark'
                :toast.type === 'success' ? 'hover:bg-vv-white'
                : ''}"
                on:click={() => removeToast(toast)}>
                <CloseIcon width=14 height=14></CloseIcon>
            </Button>
        </div>
    </div>
    {#if toast.duration}
    <div class="overflow-hidden">
        <div class="w-full h-[4px]
            {toast.type === 'failure' ? 'bg-vv-red-danger-dark'
            : 'bg-vv-teal-dark'}"
            bind:this={toast._progressRef}>
            &nbsp;
        </div>
    </div>
    {/if}
</div>
{/each}
</div>